import { type RefObject, useEffect, useRef, useState } from "react";
import { opaque } from "./contrast";
import { parseColour } from "./parse";
import type { Rgba } from "./rgba";

export interface Interference {
  id: string;
  message: string;
}

// The pair the page asked the contrast preview to be painted, which is the one box on the page painted in both.
export interface Expected {
  background: Rgba;
  colour: Rgba;
}

// What the browser says that box actually is. The two agree until something between this page and the screen has an
// opinion of its own, and the whole of this module is noticing that they have stopped agreeing.
export interface Painting {
  background: string;
  colour: string;
  // A filter anywhere above the box repaints every pixel under it without moving one computed colour, so it has to
  // be asked about separately or a dark-mode extension in its filter mode goes straight past the check below.
  filter: string | null;
  forcedColours: boolean;
}

// Watches the box while somebody is looking at the page, and says what is repainting it if anything is.
export function useInterference(preview: RefObject<HTMLElement | null>, expected: Expected): Interference | null {
  const [found, setFound] = useState<Interference | null>(null);
  // Read at check time rather than closed over, so a timer set ten seconds ago judges the colour on screen now.
  const expectedRef = useRef(expected);
  expectedRef.current = expected;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    // The wait is the load's alone: an extension that repaints on load has not necessarily finished on the first frame.
    let waited = false;

    const run = () => {
      waited = true;
      const node = preview.current;
      if (node) setFound(judgePainting(readPainting(node), expectedRef.current));
      timer = setTimeout(run, CHECK_INTERVAL);
    };

    // Nothing runs while the tab is in the background: a page nobody is looking at is not being misread by anybody,
    // and a timer there is work some other tab is paying for. Coming back is a check straight away, whatever was
    // turned on while the tab was away being on now.
    const restart = () => {
      clearTimeout(timer);
      timer = undefined;
      if (document.visibilityState === "visible") timer = setTimeout(run, waited ? 0 : FIRST_CHECK);
    };

    restart();
    document.addEventListener("visibilitychange", restart);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", restart);
    };
  }, [preview]);

  return found;
}

// The judgement is a fact about two colours and needs no browser, so it is separable from the reading that does.
export function judgePainting(painting: Painting, expected: Expected): Interference | null {
  if (painting.forcedColours) return FORCED_COLOURS;
  if (painting.filter) return FILTERED;

  const background = parseColour(painting.background);
  const colour = parseColour(painting.colour);
  // A value this page cannot read is not evidence of anything, and silence costs less here than an accusation does.
  if (!background || !colour) return null;

  const moved = hasMoved(background, opaque(expected.background)) || hasMoved(colour, expected.colour);
  return moved ? REPAINTED : null;
}

export function readPainting(node: HTMLElement): Painting {
  const painted = getComputedStyle(node);
  return {
    background: painted.backgroundColor,
    colour: painted.color,
    filter: filterAbove(node),
    forcedColours: window.matchMedia("(forced-colors: active)").matches,
  };
}

const FORCED_COLOURS: Interference = {
  id: "forced-colours",
  message:
    "Your browser is in forced-colours mode, so what is drawn here is the system palette rather than the colour being"
    + " converted. Every swatch, the contrast preview and the simulations are affected; the values in the boxes are not.",
};

const FILTERED: Interference = {
  id: "filter",
  message:
    "A filter is being drawn over this page, which is usually a dark-mode or colour-correction extension. Every swatch,"
    + " the contrast preview and the simulations are repainted before they reach you; the values in the boxes are not.",
};

const REPAINTED: Interference = {
  id: "repaint",
  message:
    "Something is rewriting the colours on this page, usually a dark-mode extension such as Dark Reader or a user"
    + " stylesheet. What is drawn here is not the colour being converted; the values in the boxes still are.",
};

// Long enough for an extension that repaints on load to have finished repainting, since a banner that comes up on
// every visit and goes again a second later is a banner nobody reads the second time.
const FIRST_CHECK = 10_000;

// Whether a page is being repainted is not a thing that changes minute to minute, and a page checking itself more
// often than that is spending somebody's battery to find out something it already knows.
const CHECK_INTERVAL = 60_000;

// Two levels a channel and a fiftieth of an opacity: far above what rounding a computed value can account for, and
// far below anything a repainting does, so neither a false alarm nor a repaint that slipped through is on the table.
const CHANNEL_SLACK = 2;
const ALPHA_SLACK = 0.02;

function hasMoved(painted: Rgba, asked: Rgba): boolean {
  return Math.abs(painted.r - asked.r) > CHANNEL_SLACK
    || Math.abs(painted.g - asked.g) > CHANNEL_SLACK
    || Math.abs(painted.b - asked.b) > CHANNEL_SLACK
    || Math.abs(painted.a - asked.a) > ALPHA_SLACK;
}

// Everything between the box and the document, since a filter set on any one of them draws the box through it.
function filterAbove(node: HTMLElement): string | null {
  for (let above: HTMLElement | null = node; above; above = above.parentElement) {
    const { filter } = getComputedStyle(above);
    if (filter && filter !== "none") return filter;
  }
  return null;
}
